/**
 * SaveToDriveModal — модалка «Добавить на диск».
 * UX: выбор диска → drill в папки → клик «Сохранить здесь» сохраняет файл
 * в текущую папку. Можно создать новую папку inline кнопкой «+ Папка».
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Icon } from "@/components/prototype/Icon";
import {
  fetchDriveList, createDriveFolder, attachToDrive,
} from "@/api/drive";
import { type ChatTopic } from "@/api/dashboard";

interface AttachmentItem {
  attachmentId: number;
  filename?: string;
}

interface Props {
  topics: ChatTopic[];
  /** Один или несколько аттачей. Все будут сохранены в выбранную папку. */
  items: AttachmentItem[];
  onClose: () => void;
  onSaved?: () => void;
}

export function SaveToDriveModal({ topics, items, onClose, onSaved }: Props) {
  const qc = useQueryClient();
  const driveTopics = useMemo(() => topics.filter(t => t.kind === "drive"), [topics]);
  const [topicId, setTopicId] = useState<number | null>(driveTopics.length === 1 ? driveTopics[0].id : null);
  const [folderId, setFolderId] = useState<number | null>(null);
  const [creatingFolder, setCreatingFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState("");
  const [saving, setSaving] = useState(false);
  const [savedHere, setSavedHere] = useState(false);
  const [query, setQuery] = useState("");

  const listQ = useQuery({
    queryKey: ["drive/list", topicId, folderId],
    queryFn: () => fetchDriveList(topicId as number, folderId),
    enabled: topicId != null,
    staleTime: 15_000,
  });

  // ESC закрывает
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const goBackToTopics = useCallback(() => {
    setTopicId(null); setFolderId(null); setCreatingFolder(false); setSavedHere(false);
  }, []);

  const goBackOneFolder = useCallback(() => {
    if (folderId != null && listQ.data?.breadcrumbs) {
      const bc = listQ.data.breadcrumbs;
      if (bc.length > 1) setFolderId(bc[bc.length - 2].id);
      else setFolderId(null);
    } else if (driveTopics.length > 1) {
      goBackToTopics();
    }
    setSavedHere(false);
  }, [folderId, listQ.data, driveTopics.length, goBackToTopics]);

  async function handleCreateFolder() {
    if (!newFolderName.trim() || topicId == null) return;
    try {
      const f = await createDriveFolder(topicId, newFolderName.trim(), folderId);
      setNewFolderName("");
      setCreatingFolder(false);
      qc.invalidateQueries({ queryKey: ["drive/list", topicId, folderId] });
      // Сразу заходим внутрь новой папки
      setFolderId(f.id);
    } catch (e) {
      alert(`Не удалось создать папку: ${(e as Error).message}`);
    }
  }

  async function handleSaveHere() {
    if (topicId == null || items.length === 0) return;
    setSaving(true);
    try {
      // Параллельно сохраняем все аттачи в выбранную папку
      await Promise.all(items.map(it =>
        attachToDrive(topicId, {
          attachment_id: it.attachmentId,
          folder_id: folderId,
          name: it.filename,
        })
      ));
      setSavedHere(true);
      qc.invalidateQueries({ queryKey: ["drive/list", topicId, folderId] });
      onSaved?.();
      setTimeout(() => onClose(), 800);
    } catch (e) {
      alert(`Не удалось сохранить: ${(e as Error).message}`);
      setSaving(false);
    }
  }

  const currentLocation = (() => {
    if (topicId == null) return "Выберите диск";
    const topicName = driveTopics.find(t => t.id === topicId)?.name ?? "Диск";
    if (!listQ.data?.breadcrumbs.length) return topicName;
    return `${topicName} / ${listQ.data.breadcrumbs.map(b => b.name).join(" / ")}`;
  })();

  const canSave = topicId != null && !saving;

  return (
    <div className="save-drive-bg" onClick={onClose}>
      <div className="save-drive-modal" onClick={(e) => e.stopPropagation()}>
        <div className="save-drive-header">
          {(topicId != null && (folderId != null || driveTopics.length > 1)) && (
            <button
              type="button"
              className="save-drive-back"
              onClick={goBackOneFolder}
              aria-label="Назад"
            >
              <Icon name="chevron_l" size={20}/>
            </button>
          )}
          <div className="save-drive-title">
            <div className="save-drive-title-main">Добавить на диск</div>
            <div className="save-drive-title-path">{currentLocation}</div>
          </div>
          <button type="button" className="save-drive-close" onClick={onClose} aria-label="Закрыть">
            <Icon name="x" size={18}/>
          </button>
        </div>

        <div className="save-drive-body">
          {savedHere && (
            <div className="save-drive-success">
              <span style={{ fontSize: 24 }}>✓</span> Сохранено {items.length > 1 ? `(${items.length} ${items.length < 5 ? "файла" : "файлов"})` : ""}
            </div>
          )}

          {topicId == null && (
            <>
              {driveTopics.length > 4 && (
                <input
                  type="search"
                  className="save-drive-search"
                  placeholder="Поиск диска…"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  autoFocus
                />
              )}
              {driveTopics.length === 0 && (
                <div className="save-drive-empty">Нет доступных дисков</div>
              )}
              {driveTopics
                .filter(t => {
                  const q = query.trim().toLowerCase();
                  if (!q) return true;
                  return (t.name ?? "").toLowerCase().includes(q);
                })
                .map(t => (
                  <button
                    key={t.id}
                    type="button"
                    className="save-drive-row"
                    onClick={() => { setTopicId(t.id); setQuery(""); }}
                  >
                    <Icon name="folder" size={22}/>
                    <span className="save-drive-row-name">{t.name}</span>
                    <span className="save-drive-row-arrow">›</span>
                  </button>
                ))}
            </>
          )}

          {topicId != null && listQ.isLoading && (
            <div className="save-drive-loading">Загрузка…</div>
          )}

          {topicId != null && !listQ.isLoading && (
            <>
              {(listQ.data?.folders ?? []).length > 6 && (
                <input
                  type="search"
                  className="save-drive-search"
                  placeholder="Поиск папки…"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  autoFocus
                />
              )}
              {(listQ.data?.folders ?? [])
                .filter(f => {
                  const q = query.trim().toLowerCase();
                  if (!q) return true;
                  return (f.name ?? "").toLowerCase().includes(q);
                })
                .map(f => (
                  <button
                    key={`fld-${f.id}`}
                    type="button"
                    className="save-drive-row"
                    onClick={() => { setFolderId(f.id); setQuery(""); }}
                  >
                    <Icon name="folder" size={22}/>
                    <span className="save-drive-row-name">{f.name}</span>
                    <span className="save-drive-row-meta">{f.items_count}</span>
                    <span className="save-drive-row-arrow">›</span>
                  </button>
                ))}
              {(listQ.data?.folders ?? []).length === 0 && !creatingFolder && (
                <div className="save-drive-hint">В этой папке нет подпапок</div>
              )}

              {/* Inline создание папки */}
              {creatingFolder ? (
                <div className="save-drive-newfolder">
                  <input
                    type="text"
                    autoFocus
                    placeholder="Название папки"
                    value={newFolderName}
                    onChange={(e) => setNewFolderName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") handleCreateFolder();
                      if (e.key === "Escape") { setCreatingFolder(false); setNewFolderName(""); }
                    }}
                  />
                  <button
                    type="button"
                    className="btn btn-sm"
                    disabled={!newFolderName.trim()}
                    onClick={handleCreateFolder}
                  >
                    Создать
                  </button>
                  <button
                    type="button"
                    className="btn btn-sm btn-secondary"
                    onClick={() => { setCreatingFolder(false); setNewFolderName(""); }}
                  >
                    <Icon name="x" size={14}/>
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  className="save-drive-row save-drive-row-action"
                  onClick={() => setCreatingFolder(true)}
                >
                  <Icon name="plus" size={20}/>
                  <span className="save-drive-row-name">Новая папка</span>
                </button>
              )}
            </>
          )}
        </div>

        {topicId != null && (
          <div className="save-drive-actions">
            <button type="button" className="btn btn-sm btn-secondary" onClick={onClose}>
              Отмена
            </button>
            <button
              type="button"
              className="btn btn-sm"
              disabled={!canSave}
              onClick={handleSaveHere}
            >
              {saving ? "Сохранение…" : items.length > 1 ? `Сохранить ${items.length} файла здесь` : "Сохранить здесь"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
